import { Mesh2MotionEngine } from './Mesh2MotionEngine'
import { ProcessStep } from './lib/enums/ProcessStep'
import { CustomSkeletonStorage } from './features/persistence/CustomSkeletonStorage'
import { type SkeletonDescriptor } from './features/skeleton/SkeletonDescriptor'
import { confirm_action } from './lib/ConfirmationDialog.ts'
import { ModalDialog } from './lib/ModalDialog.ts'
import { escape_html } from './lib/html.ts'

/**
 * Standalone entry for the Build Skeleton mode (the /build-skeleton.html page).
 *
 * Renders a small library list of the user's saved custom skeletons. Clicking
 * a row opens it for editing; clicking "+ New Skeleton" opens a fresh editor.
 * Save returns to the library list with the new/updated skeleton present.
 */
class BuildSkeletonBootstrap {
  private readonly engine: Mesh2MotionEngine

  constructor () {
    this.engine = new Mesh2MotionEngine()
    this.engine.addEventListener('build-skeleton-saved', ((_e: CustomEvent<SkeletonDescriptor>) => {
      void this.show_library()
    }) as EventListener)
    this.engine.addEventListener('build-skeleton-cancelled', () => {
      void this.show_library()
    })

    document.addEventListener('DOMContentLoaded', () => {
      void this.show_library()
      const new_btn = document.querySelector<HTMLButtonElement>('#build-skeleton-library-new')
      new_btn?.addEventListener('click', () => this.open_editor(undefined))
    })
  }

  private async show_library (): Promise<void> {
    const library = document.querySelector<HTMLDivElement>('#build-skeleton-library')
    const tools = document.querySelector<HTMLDivElement>('#build-skeleton-tools')
    if (library === null || tools === null) return

    tools.style.display = 'none'
    library.style.display = 'flex'

    const list_el = document.querySelector<HTMLUListElement>('#build-skeleton-library-list')
    if (list_el !== null) {
      list_el.innerHTML = ''
      const items = await CustomSkeletonStorage.list()
      if (items.length === 0) {
        const empty = document.createElement('li')
        empty.textContent = 'No saved skeletons yet.'
        empty.style.opacity = '0.7'
        empty.style.padding = '0.5rem 0'
        list_el.appendChild(empty)
      }
      for (const descriptor of items) {
        const li = document.createElement('li')
        li.style.display = 'flex'
        li.style.alignItems = 'center'
        li.style.gap = '0.5rem'
        li.style.padding = '0.25rem 0'

        const thumb = document.createElement('div')
        thumb.style.width = '48px'
        thumb.style.height = '36px'
        thumb.style.flex = '0 0 auto'
        thumb.style.background = '#2a2a2a'
        thumb.style.borderRadius = '3px'
        thumb.style.backgroundSize = 'cover'
        thumb.style.backgroundPosition = 'center'
        if (descriptor.thumbnail_data_url !== undefined) {
          thumb.style.backgroundImage = `url("${descriptor.thumbnail_data_url}")`
        }
        li.appendChild(thumb)

        const name_btn = document.createElement('button')
        name_btn.className = 'no-style-button'
        name_btn.textContent = descriptor.name
        name_btn.style.flex = '1'
        name_btn.style.textAlign = 'left'
        name_btn.addEventListener('click', () => this.open_editor(descriptor))

        const delete_btn = document.createElement('button')
        delete_btn.className = 'secondary-button'
        delete_btn.textContent = 'Delete'
        delete_btn.addEventListener('click', () => {
          void confirm_action({
            title: 'Delete skeleton',
            message: `Delete skeleton "${descriptor.name}"?`,
            confirm_label: 'Delete',
            danger: true
          }).then(async (confirmed) => {
            if (!confirmed) return
            const deleted = await CustomSkeletonStorage.delete(descriptor.id)
            if (!deleted) {
              new ModalDialog('Delete failed', `Could not delete skeleton "${escape_html(descriptor.name)}". Your browser storage may be unavailable or blocked.`).show()
              return
            }
            await this.show_library()
          })
        })

        li.appendChild(name_btn)
        li.appendChild(delete_btn)
        list_el.appendChild(li)
      }
    }
  }

  private open_editor (initial: SkeletonDescriptor | undefined): void {
    const library = document.querySelector<HTMLDivElement>('#build-skeleton-library')
    if (library !== null) library.style.display = 'none'
    this.engine.process_step_changed(ProcessStep.BuildSkeleton)
    if (initial !== undefined) {
      this.engine.begin_build_skeleton_step(initial)
    }
  }
}

new BuildSkeletonBootstrap()
